/* @jsx h */
import {
  defineComponent,
  ref,
  onMounted,
  h,
  watch,
  provide,
  computed,
} from "vue";
import { api } from "@app/data/api";
import {
  PaymentsSummaryReport,
  OrdersHourlyReportDatum,
  TopProductsDatum,
  TopCategoriesDatum,
  OrdersBreakdown,
  Shift,
} from "@lemmonhq/types";
import { OverviewFilters, type OverviewFiltersProps } from "./OverviewFilters";
import { OverviewChart } from "./OverviewChart";
import { MobileOverviewHeader } from "./components/MobileOverviewHeader";
import { OpenTablesBar } from "./components/OpenTablesBar";
import { OpenTablesSheet } from "./components/OpenTablesSheet";
import { useOpenTables } from "./composables/useOpenTables";
import { RevenueComparison } from "./components/RevenueComparison";
import { RevenueBridge } from "./components/RevenueBridge";
import { useTrpc } from "@app/vue/providers/index";
import type {
  RevenueBridge as RevenueBridgeData,
  RevenueComparison as RevenueComparisonData,
} from "@app/api/trpc/routers/overview-charts";
import { getDateRange, syncedRef } from "@lemmonhq/uikit";
import { sharedDateRangeKey } from "@app/vue/composables/sharedDateRangeKey";
import { Loading } from "@app/vue/components/UIComponents";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useAuth } from "@app/vue/providers/Auth";
import { useLocalization } from "@lemmonhq/uikit";
import {
  serializeFiltersToQuery,
  deserializeFiltersFromQuery,
  formatPriceInLocale,
} from "@lemmonhq/orders";

export const OverviewPage = defineComponent<{ hideFilters?: boolean }>({
  name: "OverviewPage",
  props: ["hideFilters"],
  setup(props) {
    const loading = ref(true);
    const summaryData = ref<PaymentsSummaryReport>();
    const hourlyData = ref<OrdersHourlyReportDatum[]>([]);
    const ordersBreakdown = ref<OrdersBreakdown>();
    const topProducts = ref<TopProductsDatum[]>([]);
    const topCategories = ref<TopCategoriesDatum[]>([]);
    const fetchError = ref(false);
    const auth = useAuth();
    const { t } = useLocalization();
    const activeShiftRef = ref<Shift | null>(null);
    const mobileRevenueScrollProgress = ref(0);

    // The user filter is backed by the colleagues already present on the auth
    // session (plus the current user), so no extra fetch is needed. `id` here is
    // the user id, which is what payments/orders are attributed to.
    const users = computed<{ id: number; name: string }[]>(() => {
      const currentUser = auth.user.value;
      if (!currentUser) return [];
      const entries = [
        { id: currentUser.id, name: currentUser.profile.name },
        ...(currentUser.colleagues || [])
          // Skip deactivated/removed colleagues.
          .filter((colleague) => !colleague.profile.deleted_at)
          .map((colleague) => ({
            id: colleague.id,
            name: colleague.profile.name,
          })),
      ];
      const seen = new Set<number>();
      return entries.filter((entry) => {
        if (seen.has(entry.id)) return false;
        seen.add(entry.id);
        return true;
      });
    });
    provide("mobileRevenueScrollProgress", mobileRevenueScrollProgress);

    // The two journal-backed charts load on their own track. They must never
    // be able to blank the page that already works: a failure here leaves the
    // rest of the overview intact and simply hides the two cards.
    const trpc = useTrpc();
    const bridgeData = ref<RevenueBridgeData | null>(null);
    const comparisonData = ref<RevenueComparisonData | null>(null);
    const chartsLoading = ref(true);

    // Revenue reporting is manager-only server-side, so a staff user would get
    // a 403. Skip the request rather than firing one we know will be refused.
    const canSeeRevenueCharts = computed(
      () =>
        auth.user.value?.role === "manager" ||
        auth.user.value?.role === "admin",
    );

    const route = useRoute();
    const router = useRouter();

    // userIds live in the query as a comma-separated list; the generic filter
    // (de)serializers only cover primitives, so this one is handled by hand.
    const parseUserIdsFromQuery = (): number[] => {
      const raw = route.query.userIds;
      if (typeof raw !== "string" || raw.length === 0) {
        return [];
      }
      return raw
        .split(",")
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));
    };

    // Shared with the other report pages and persisted, so all of them open on
    // the range the user last picked.
    const syncedDateRange = syncedRef(
      sharedDateRangeKey(
        auth.user.value?.restaurant_id,
        auth.user.value?.id,
      ),
      {
        type: "today",
        ...getDateRange("today"),
      },
    );

    /**
     * A link that names a range wins over the shared one — otherwise opening a
     * shared report URL would silently show a different period than it did for
     * the person who sent it.
     */
    const queryHasDateRange = () =>
      typeof route.query.dateRange === "string" ||
      (route.query.dateRangeType === "custom" &&
        typeof route.query.startDate === "string" &&
        typeof route.query.endDate === "string");

    const resolveInitialFilters = (): OverviewFiltersProps["filters"] => {
      const baseFilters = deserializeFiltersFromQuery(
        route.query,
        getDateRange,
        {
          booleanFields: ["activeShiftOnly"],
          defaultDateRangeType: "today",
        },
      );

      // Apply role-based defaults for activeShiftOnly
      const activeShiftOnly =
        auth.user.value?.role === "manager"
          ? false
          : (baseFilters.activeShiftOnly ?? true);

      // Staff cannot change the range (the picker is disabled for them), so
      // never carry one in from the URL or the range shared with the other
      // report pages: it would label the page with a period they did not pick
      // and widen the charts that are not scoped to their shift.
      if (auth.user.value?.role === "staff") {
        return {
          dateRange: { type: "today", ...getDateRange("today") },
          activeShiftOnly,
          userIds: [],
        };
      }

      return {
        dateRange: queryHasDateRange()
          ? (baseFilters.dateRange as any)
          : syncedDateRange.value,
        activeShiftOnly,
        userIds: parseUserIdsFromQuery(),
      };
    };

    const filters = ref<OverviewFiltersProps["filters"]>(
      resolveInitialFilters(),
    );

    // Payments only land in the report once a table is settled, so a range that
    // covers the running shift is missing whatever is still open on tables.
    const openTables = useOpenTables();
    const openTablesSheetOpen = ref(false);

    const showOpenTablesBar = () =>
      !openTables.error.value && openTables.busyCount.value > 0;

    const buildQueryFromFilters = () => {
      const query = serializeFiltersToQuery(filters.value, {
        defaultDateRangeType: "today",
      });
      if (filters.value.userIds.length > 0) {
        query.userIds = filters.value.userIds.join(",");
      }
      return query;
    };

    const isSameQuery = (nextQuery: Record<string, string>) => {
      const currentEntries = Object.entries(route.query).filter(
        ([, value]) => typeof value === "string",
      ) as [string, string][];
      if (currentEntries.length !== Object.keys(nextQuery).length) {
        return false;
      }
      return currentEntries.every(([key, value]) => nextQuery[key] === value);
    };

    const updateQueryFromFilters = () => {
      const query = buildQueryFromFilters();
      if (isSameQuery(query)) {
        return false;
      }

      router.replace({
        query,
      });
      return true;
    };

    const applyFilters = (newFilters: OverviewFiltersProps["filters"]) => {
      // Persist before anything else so a page the user navigates to next
      // reads the new range even if the query write is a no-op.
      syncedDateRange.value = newFilters.dateRange;
      filters.value = newFilters;
      const changed = updateQueryFromFilters();
      if (!changed) {
        fetchData();
      }
    };

    const updateDateRange = (
      dateRange: OverviewFiltersProps["filters"]["dateRange"],
    ) => {
      applyFilters({ ...filters.value, dateRange, activeShiftOnly: false });
    };

    /**
     * Generation counter guarding against out-of-order responses: change the
     * date range twice quickly and the first request can resolve last, which
     * would paint stale numbers over fresh ones.
     */
    let chartsGeneration = 0;

    const fetchCharts = async () => {
      // The journal-backed comparison/bridge charts are not scoped by staff, so
      // they are hidden entirely while a user filter is active — no point
      // fetching them then either.
      if (!canSeeRevenueCharts.value || filters.value.userIds.length > 0) {
        bridgeData.value = null;
        comparisonData.value = null;
        chartsLoading.value = false;
        return;
      }

      const generation = ++chartsGeneration;
      chartsLoading.value = true;
      const range = {
        from: filters.value.dateRange.startDate,
        to: filters.value.dateRange.endDate,
      };

      // Settled, not all: one chart failing must not blank the other.
      const [bridge, comparison] = await Promise.allSettled([
        trpc.overviewCharts.bridge.query(range),
        trpc.overviewCharts.comparison.query(range),
      ]);

      if (generation !== chartsGeneration) return;

      bridgeData.value = bridge.status === "fulfilled" ? bridge.value : null;
      comparisonData.value =
        comparison.status === "fulfilled" ? comparison.value : null;

      if (bridge.status === "rejected") {
        console.error("Failed to load revenue bridge:", bridge.reason);
      }
      if (comparison.status === "rejected") {
        console.error("Failed to load revenue comparison:", comparison.reason);
      }
      chartsLoading.value = false;
    };

    const fetchData = async () => {
      loading.value = true;
      fetchError.value = false;
      // Fire alongside the main report rather than after it — independent
      // request, and awaiting it here would delay the page for no reason.
      void fetchCharts();
      // Busy tables matter regardless of shift hours and load independently.
      void openTables.refresh();
      try {
        let shiftId: string | undefined;
        try {
          const activeShift = await api.getActiveShift();
          activeShiftRef.value = activeShift;
          shiftId = filters.value.activeShiftOnly ? activeShift?.id : undefined;
        } catch (e) {
          filters.value.activeShiftOnly = false;
        }

        // If staff user and no active shift, set zero values
        if (auth.user.value?.role === "staff" && !activeShiftRef.value) {
          summaryData.value = {
            total_amount: 0,
            total_count: 0,
            discount_amount: 0,
            discount_count: 0,
            cash_amount: 0,
            cash_count: 0,
            card_amount: 0,
            card_count: 0,
            twint_amount: 0,
            twint_count: 0,
            online_amount: 0,
            online_count: 0,
            manual_amount: 0,
            manual_count: 0,
            total_tips: 0,
          };
          hourlyData.value = [];
          ordersBreakdown.value = {
            by_service_method: null,
            by_status: null,
          };
          topProducts.value = [];
          topCategories.value = [];
        } else {
          const userIds = filters.value.userIds;
          const [summary, hourly, breakdown, products, categories] =
            await Promise.all([
              api.getPaymentsSummary(
                {
                  p_start_date: filters.value.dateRange.startDate,
                  p_end_date: filters.value.dateRange.endDate,
                  p_shift_id: shiftId,
                },
                userIds,
              ),
              api.getOrdersHourlyBreakdown(filters.value.dateRange, userIds),
              api.getOrdersBreakdown(filters.value.dateRange, shiftId, userIds),
              api.getTopProducts(filters.value.dateRange, userIds),
              api.getTopCategories(filters.value.dateRange, userIds),
            ]);
          summaryData.value = summary;
          hourlyData.value = hourly;
          ordersBreakdown.value = breakdown;
          topProducts.value = products;
          topCategories.value = categories;
        }
      } catch (error) {
        fetchError.value = true;
        console.error("Failed to fetch reports data:", error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      updateQueryFromFilters();
      fetchData();
    });

    watch(
      () => route.query,
      () => {
        filters.value = resolveInitialFilters();
        fetchData();
      },
    );

    return () => (
      <div class="w-full  flex flex-col overflow-auto">
        {!props.hideFilters ? (
          <MobileOverviewHeader
            dateRange={filters.value.dateRange}
            onDateRangeChange={updateDateRange}
            revenue={
              summaryData.value
                ? formatPriceInLocale(
                    summaryData.value.total_amount,
                    false,
                    0,
                    auth.user.value?.restaurant.currency || "CHF",
                  )
                : undefined
            }
            restaurantName={auth.user.value?.restaurant.name}
            canFilter={auth.user.value?.role !== "staff"}
            currency={auth.user.value?.restaurant.currency || "CHF"}
          />
        ) : null}
        {!props.hideFilters ? (
          <div class="hidden px-2 py-2 xl:px-4 md:flex flex-col items-start gap-2 lg:flex-row lg:items-center justify-between border-b glossy-bg sticky top-0 z-[1]">
            <div class="flex w-full flex-1 items-center justify-end gap-2 lg:justify-start">
              <div class="py-0">
                <OverviewFilters
                  filters={filters.value}
                  users={users.value}
                  onFiltersChange={applyFilters}
                />
              </div>
            </div>
            <div class="hidden lg:flex justify-start items-center gap-5">
              <button disabled class="btn btn-outline btn-sm gap-2 hidden">
                <span class="material-symbols-outlined">print</span>
                {t("general.words.print")}
              </button>
              <RouterLink
                to={"/app/reports/closing_reports"}
                class="btn btn-primary btn-sm gap-2"
              >
                <span class="material-symbols-outlined">sunny</span>
                {t("reports.dashboard.close_shift_button")}
              </RouterLink>
              <button
                onClick={() => fetchData()}
                class="btn btn-outline btn-sm gap-2 "
              >
                <span class="material-symbols-outlined">refresh</span>
              </button>
            </div>
          </div>
        ) : null}
        {showOpenTablesBar() ? (
          <OpenTablesBar
            busyCount={openTables.busyCount.value}
            freeCount={openTables.freeCount.value}
            orderCount={openTables.orderCount.value}
            totalAmount={openTables.totalAmount.value}
            currency={auth.user.value?.restaurant.currency || "CHF"}
            loading={openTables.loading.value}
            onOpen={() => {
              openTablesSheetOpen.value = true;
            }}
          />
        ) : null}
        <OpenTablesSheet
          isOpen={openTablesSheetOpen.value}
          onClose={() => {
            openTablesSheetOpen.value = false;
          }}
        />
        <div class="flex-1 py-4 px-2 xl:px-4">
          {fetchError.value ? (
            <div class="h-full w-full flex flex-col space-y-4 items-center justify-center">
              <div class="text-3xl text-prominent">
                {t("reports.overview.error_title")}
              </div>
              <div class="text-1xl">{t("reports.overview.error_desc")}</div>
            </div>
          ) : !loading.value && summaryData.value && ordersBreakdown.value ? (
            <div class="space-y-4 md:space-y-6">
              <OverviewChart
                data={summaryData.value}
                hourlyData={hourlyData.value}
                ordersBreakdown={ordersBreakdown.value}
                topProducts={topProducts.value}
                topCategories={topCategories.value}
                loading={loading.value}
                dateRange={filters.value.dateRange as any}
                currency={auth.user.value?.restaurant.currency || "CHF"}
                showDetailLinks={true}
              />
              {canSeeRevenueCharts.value &&
              filters.value.userIds.length === 0 ? (
                // Side by side on a wide screen, stacked on a phone. Each card
                // owns its own empty and loading state, so a failure on one
                // leaves the other readable.
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 md:gap-6">
                  <RevenueComparison
                    data={comparisonData.value}
                    currency={auth.user.value?.restaurant.currency || "CHF"}
                    loading={chartsLoading.value}
                  />
                  <RevenueBridge
                    data={bridgeData.value}
                    currency={auth.user.value?.restaurant.currency || "CHF"}
                    loading={chartsLoading.value}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div class="h-full w-full flex items-center justify-center">
              <Loading />
            </div>
          )}
        </div>
      </div>
    );
  },
});
